import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { authApi } from '@/lib/auth-api';
import { googleRedirectUri, rememberGoogleState } from '@/modules/auth/google-oauth';

/**
 * "Continue with Google".
 *
 * ## It hides itself when it cannot work
 *
 * The button asks the API whether this installation has Google credentials, and renders nothing if
 * not. A sign-in button that appears on every deployment and fails on click for anybody who has not
 * configured it is worse than no button — it reads as a broken product rather than an unconfigured
 * one, and it is the first thing a new customer touches.
 *
 * ## The state parameter
 *
 * Minted by the server, stored here, and compared when Google sends the browser back. Without it,
 * somebody can run the flow with their own account, capture the callback URL, and hand it to a
 * victim — who is then signed into the attacker's account without noticing. `sessionStorage` rather
 * than `localStorage` so it dies with the tab and cannot linger across sessions.
 */
export function GoogleSignInButton({ label }: { label: string }) {
  const [starting, setStarting] = useState(false);

  const availability = useQuery({
    queryKey: ['google-signin'],
    queryFn: () => authApi.googleStart(googleRedirectUri()),
    // One answer per page load is plenty; this changes when the operator edits a config file.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const ready = availability.data;

  if (ready === undefined || !ready.available) {
    return null;
  }

  const go = () => {
    setStarting(true);

    if (!rememberGoogleState(ready.state)) {
      // A browser refusing storage cannot complete the check on the way back, so the sign-in
      // would be rejected there. Better to stop here, where there is still something to say.
      setStarting(false);

      return;
    }

    window.location.assign(ready.url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button variant="outline" className="w-full" onClick={go} disabled={starting}>
        {starting ? (
          <Loader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <GoogleMark className="mr-2 size-4" />
        )}
        {label}
      </Button>
    </div>
  );
}

/** Google's mark, inline so the page makes no third-party request before anybody has agreed to one. */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
